import type { ReactNode } from "react"
import { cn } from "@/lib/utils"

// Titled content section — a section TITLE with an optional description, then the content.
//
// Default (borderless): a 17px medium title, a 14px muted description, and NO rule under
// them. The rule used to be there because the content below was bare rows that needed a top
// edge; in the current register the content is a card, and a hairline immediately above a
// card's own hairline reads as a double border.
//
// `bordered`: wraps everything in a `rounded-lg border` card and keeps the header rule, which
// there separates a card header from a card body. Content renders flush; the caller controls
// inner padding (a table sits edge-to-edge, prose adds its own).
export function Section({
  title,
  description,
  actions,
  bordered = false,
  className,
  contentClassName,
  children,
}: {
  title?: ReactNode
  description?: string
  actions?: ReactNode
  bordered?: boolean
  className?: string
  contentClassName?: string
  children: ReactNode
}) {
  const hasHeader = Boolean(title || description || actions)

  const header = hasHeader ? (
    <div
      className={cn("flex items-center justify-between gap-4", bordered ? "border-b border-border px-5 py-3.5" : "")}
    >
      <div className="min-w-0">
        {title ? (
          <h2 className={cn("leading-tight font-medium", bordered ? "text-[15px]" : "text-[17px]")}>{title}</h2>
        ) : null}
        {description ? <div className="mt-1 text-[13px] text-muted-foreground">{description}</div> : null}
      </div>
      {actions ? <div className="shrink-0">{actions}</div> : null}
    </div>
  ) : null

  if (bordered) {
    return (
      <section className={cn("min-w-0 overflow-hidden rounded-lg border border-border bg-card", className)}>
        {header}
        <div className={contentClassName}>{children}</div>
      </section>
    )
  }

  return (
    <section className={cn("min-w-0 space-y-3", className)}>
      {header}
      <div className={contentClassName}>{children}</div>
    </section>
  )
}
